import { ApolloClient, type DocumentNode, TypedDocumentNode, useApolloClient } from '@apollo/client';
import { useQuery } from '@redwoodjs/web';
import type { PageInfo, PaginationResult } from '@shared/types/index';
import { useMemo, useRef } from 'react';
import { type PagedGridQueryState, pagedGridInput } from '../rows/useGridRows';
import { type PaginatedExportInputOverrides, type PaginationPage, PaginationWebResult } from './types';

export type ServerEnhancedGridQueryArgs = {
  queryKey: string;
  query: TypedDocumentNode;
  gridState: PagedGridQueryState;
  extraVariables?: Readonly<Record<string, unknown>>;
};

/**
 * Rows per export request. `findManyPaginated` buffers a whole page in API memory
 * before serialising it, so this is the API's peak-per-request row count.
 */
export const EXPORT_PAGE_SIZE = 1_000;

/**
 * Hard stop on the row array the export accumulates in the browser. Above it the
 * export refuses; it never returns a short file that reads as complete.
 */
export const EXPORT_ROW_CEILING = 100_000;

export const exportCeilingMessage = (ceiling: number) =>
  `This export is larger than ${ceiling.toLocaleString()} rows. Narrow it with filters or a date range, then export again.`;

type PagedRequest = { after?: string | null; first?: number | null; sortItems?: unknown };

/** One page request's identity: the cursor window and sort it was asked for. */
export const pagedRequestKey = ({ after, first, sortItems }: PagedRequest) =>
  JSON.stringify({ after: after ?? null, first: first ?? null, sortItems: sortItems ?? [] });

const pageInfoRequests = new WeakMap<PageInfo, string>();

/** The request a `pageInfo` from this hook answers; `undefined` for a host-built `pageInfo`. */
export const pageInfoRequestKey = (pageInfo: PageInfo) => pageInfoRequests.get(pageInfo);

/** Records which request a `pageInfo` answers, so the paged grid files its cursor only for the latest one. */
export function notePageInfoRequest(pageInfo: PageInfo, input: PagedRequest): void {
  pageInfoRequests.set(pageInfo, pagedRequestKey(input));
}

/**
 * The paged grid's request for its query state: the cursor window, sort, filters and search, with the
 * one cursor minted under a previous scope stripped. `queryScopeKey` is that scope (everything but paging).
 */
export function usePagedGridRequest(
  gridState: PagedGridQueryState,
  extraVariables?: Readonly<Record<string, unknown>>
) {
  const queryScopeKey = JSON.stringify({
    extraVariables,
    sortItems: gridState.sortItems,
    gridFilters: gridState.gridFilters,
    searchText: gridState.searchText,
    quickFilters: gridState.quickFilterOption?.filters,
  });
  const queryScopeRef = useRef(queryScopeKey);
  // Reject exactly the one cursor minted under the previous scope — never latch 'strip until the grid hands back `undefined`': `PagedDataGrid` keys its page reset on `paginationResetKey`, which never sees `extraVariables`, so a latch strips `after` FOREVER and paging dies.
  const rejectedCursorRef = useRef<string | undefined>();

  if (queryScopeRef.current !== queryScopeKey) {
    queryScopeRef.current = queryScopeKey;
    rejectedCursorRef.current = gridState.after;
  }

  if (gridState.after !== rejectedCursorRef.current) {
    rejectedCursorRef.current = undefined;
  }

  const after = rejectedCursorRef.current === undefined ? gridState.after : undefined;

  const { first, sortItems, gridFilters, searchText, quickFilterOption } = gridState;
  const input = useMemo(
    () => pagedGridInput({ first, sortItems, gridFilters, searchText, quickFilterOption }, after),
    [first, after, sortItems, gridFilters, searchText, quickFilterOption]
  );

  return { input, queryScopeKey };
}

export default function useServerEnhancedGridQuery<T = any, TPage extends PaginationPage<T> = PaginationResult<T>>({
  queryKey,
  query,
  gridState,
  extraVariables,
}: ServerEnhancedGridQueryArgs): PaginationWebResult<T, TPage> {
  const client = useApolloClient();
  const { input, queryScopeKey } = usePagedGridRequest(gridState, extraVariables);
  const responseScopeByPageRef = useRef(new WeakMap<object, string>());

  const variables = useMemo(() => ({ ...(extraVariables ?? {}), input }), [extraVariables, input]);

  const { data, loading, error, refetch } = useQuery<{ [key: string]: TPage }>(query, {
    variables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const requestSucceeded = !loading && !error;
  const pageData = data?.[queryKey];
  // Apollo can retain the previous scope's result while new variables resolve. Associate each
  // page object on first observation so cached current-scope rows remain available while loading.
  if (pageData && !responseScopeByPageRef.current.has(pageData)) {
    responseScopeByPageRef.current.set(pageData, queryScopeKey);
  }
  const isCurrentScopeResponse = pageData ? responseScopeByPageRef.current.get(pageData) === queryScopeKey : false;

  // Once we have current-scope data, don't show loading skeleton on refetches (cache-and-network).
  // This allows noRowsOverlay to render when rows=0 instead of being blocked by loadingOverlay.
  const isInitialLoad = loading && !isCurrentScopeResponse;

  return useMemo(() => {
    const mapped = mapServerPaginatedToGridData<T, TPage>(queryKey, data, requestSucceeded, isCurrentScopeResponse);
    if (mapped.pageInfo) notePageInfoRequest(mapped.pageInfo, input);
    return {
      ...mapped,
      loading: isInitialLoad,
      error,
      fetchAllForExport: createExportFunction<T>({ client, query, queryKey, variables, input }),
      refetch: () => refetch(variables),
    };
  }, [
    queryKey,
    data,
    requestSucceeded,
    isCurrentScopeResponse,
    isInitialLoad,
    error,
    client,
    query,
    variables,
    input,
    refetch,
  ]);
}

type MappedPage<T, TPage extends PaginationPage<T>> = Pick<
  PaginationWebResult<T, TPage>,
  'rows' | 'totalCount' | 'pageInfo' | 'pageData'
>;

function mapServerPaginatedToGridData<T, TPage extends PaginationPage<T>>(
  queryKey: string,
  paginationResult: Record<string, TPage> | undefined,
  requestSucceeded: boolean,
  isCurrentScopeResponse: boolean
): MappedPage<T, TPage> {
  const data = isCurrentScopeResponse ? paginationResult?.[queryKey] : undefined;
  const nodes = data?.edges?.map((edge) => edge.node) ?? [];
  return {
    rows: nodes,
    totalCount: data?.totalCount ?? null,
    pageData: data,
    pageInfo: data && requestSucceeded ? normalizePageInfo(data.pageInfo) : undefined,
  };
}

export function normalizePageInfo(pageInfo: PaginationPage<unknown>['pageInfo']): PageInfo {
  return { endCursor: pageInfo?.endCursor, hasNextPage: pageInfo?.hasNextPage ?? undefined };
}

/** The export walk treats GraphQL cursors as opaque strings and only echoes them back. */
type ExportPage<T> = {
  edges?: Array<{ node: T; cursor?: string | null }> | null;
  pageInfo?: { endCursor?: string | null; hasNextPage?: boolean | null } | null;
};

type ExportFunctionArgs = {
  client: ApolloClient<object>;
  /** Any list document: the walk only echoes `variables.input` back with its own cursor. */
  query: DocumentNode;
  queryKey: string;
  variables: Readonly<Record<string, unknown>>;
  input: Record<string, unknown>;
};

export function createExportFunction<T>({
  client,
  query,
  queryKey,
  variables,
  input,
}: ExportFunctionArgs): (inputOverrides?: PaginatedExportInputOverrides) => Promise<T[]> {
  return async (inputOverrides = {}) => {
    const rows: T[] = [];
    const seen = new Set<string>();
    let after: string | null = null;

    for (;;) {
      const page: ExportPage<T> | undefined = (
        await client.query<Record<string, ExportPage<T>>>({
          query,
          variables: { ...variables, input: { ...input, ...inputOverrides, first: EXPORT_PAGE_SIZE, after } },
          fetchPolicy: 'no-cache',
        })
      ).data?.[queryKey];
      const edges = page?.edges ?? [];

      for (const edge of edges) {
        // The window shifts under concurrent writes, so a row can land on two pages.
        if (edge.cursor != null && seen.has(edge.cursor)) continue;
        if (edge.cursor != null) seen.add(edge.cursor);
        rows.push(edge.node);
      }

      if (rows.length > EXPORT_ROW_CEILING) throw new Error(exportCeilingMessage(EXPORT_ROW_CEILING));

      const endCursor: string | null = page?.pageInfo?.endCursor ?? null;
      // A page that advances no cursor ends the walk — otherwise a server still
      // reporting hasNextPage would spin here forever.
      const advanced = edges.length > 0 && endCursor != null && endCursor !== after;
      if (!page?.pageInfo?.hasNextPage || !advanced) break;

      after = endCursor;
    }

    return rows;
  };
}
